import { cloneDeep, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { IProposalMotorDetails, IProposalPageFormState } from './form';
import { IProposalDetailsSummary } from './index2';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { getModelsByBrand, validateMotorVignette } from '../queries';
import { lookupListAsRecordObject } from '@utils/graph-utils';
import { useParams } from 'react-router-dom';
import { validateMotorDetailsField } from '../validation';
import DataService from '@services/DataService';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import { filteredBodyTypeList, filteredUsageTypeList } from '../utils';
import DateService from '@services/DateService';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';

interface IProposalMotorDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: React.Dispatch<
    React.SetStateAction<IProposalPageFormState>
  >;
  disabledForm?: boolean;
  lovs: Record<string, Record<string, string>>;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
  },
  section: {
    padding: '23px 0 35px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(4, 23.5%)`,
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  labelHeader: {
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
    margin: '0px!important',
    padding: '0px!important',
  },
  labelDropdown: {
    color: MAIN_THEME.color.black,
    margin: '5px 0 0 0 !important',
    padding: '0px!important',
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
}));

const ProposalMotorDetailsWidget: React.FC<
  IProposalMotorDetailsWidgetProps
> = ({ pageState, onPageStateUpdate, disabledForm, data, lovs }) => {
  const { classes } = useStyles();
  const params = useParams();

  const values = pageState.values.motorDetails;
  const errors = pageState.errors.motorDetails;
  const touched = pageState.touched.motorDetails;

  const [getModelsByBrandsLazy] = useLazyQuery(getModelsByBrand());

  const [modelLovs, setModelLovs] =
    useState<Record<string, Record<string, string>>>(null);
  const [loadingNameOnLicenseArState, setLoadingNameOnLicenseArState] =
    useState<boolean>(false);
  const [filteredUsageTypes, setFilteredUsageTypes] = useState<
    Record<string, string>
  >(lovs.usageTypes);
  const [filteredBodyTypes, setFilteredBodyTypes] = useState<
    Record<string, string>
  >(lovs.bodyTypes);

  const [validateVignetteLazy] = useMutation(validateMotorVignette(), {});

  const initialize = async () => {
    if (values.brand) {
      const result = await getModelsByBrandsLazy({
        variables: { modelId: values.brand || null },
      });
      const models = result.data;
      const modelsToLookup = lookupListAsRecordObject(
        models?.Insurance?.lookups?.models
      );
      setModelLovs({ model: modelsToLookup });
    }
    if (values.plateCode) {
      const filteredUsage = filteredUsageTypeList(
        values.plateCode,
        lovs.usageTypes
      );
      setFilteredUsageTypes(filteredUsage);
    }

    if (values.usageType) {
      const filteredBody = filteredBodyTypeList(
        values.usageType,
        lovs.bodyTypes
      );
      setFilteredBodyTypes(filteredBody);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldBlur = async (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.motorDetails[fieldName] = true;

    if (fieldName === 'brand') {
      if (values.brand) {
        const result = await getModelsByBrandsLazy({
          variables: { modelId: values.brand || null },
        });
        const models = result.data;
        const modelsToLookup = lookupListAsRecordObject(
          models?.Insurance?.lookups?.models
        );
        setModelLovs({ model: modelsToLookup });
      }
    }

    if (fieldName === 'vignetteCode') {
      if (!isEmpty(values.vignette)) {
        validateVignetteLazy({
          variables: {
            businessPartnerID: data.PrimaryBPID.Id,
            entityId: params.id,
            effectiveDate: DateService.formatDateBackend(
              data.PolicyEffectiveDate
            ),
            vignetteCode: values.vignetteCode,
            vignette: Number(values.vignette),
          },
        }).catch((error) => {
          if (
            error instanceof ApolloError &&
            error.message === 'Vignette Number is not assigned to you'
          ) {
            const vignetteAlreadyExistsErrorMessage = error.message;

            newPageState.errors.motorDetails['vignetteCode'] =
              vignetteAlreadyExistsErrorMessage;
          } else if (
            error instanceof ApolloError &&
            error.message === 'Vignette already in use'
          ) {
            const vignetteAlreadyExistsErrorMessage = error.message;

            newPageState.errors.motorDetails['vignetteCode'] =
              vignetteAlreadyExistsErrorMessage;
          } else {
            newPageState.errors.motorDetails['vignetteCode'] = '';
          }
        });
      }
    }
    if (fieldName === 'vignette') {
      validateVignetteLazy({
        variables: {
          businessPartnerID: data.PrimaryBPID.Id,
          entityId: params.id,
          effectiveDate: DateService.formatDateBackend(
            data.PolicyEffectiveDate
          ),
          vignetteCode: values.vignetteCode,
          vignette: Number(values.vignette),
        },
      }).catch((error) => {
        if (
          error instanceof ApolloError &&
          error.message === 'Vignette Number is not assigned to you'
        ) {
          const vignetteAlreadyExistsErrorMessage = error.message;

          newPageState.errors.motorDetails['vignette'] =
            vignetteAlreadyExistsErrorMessage;
        } else if (
          error instanceof ApolloError &&
          error.message === 'Vignette already in use'
        ) {
          const vignetteAlreadyExistsErrorMessage = error.message;

          newPageState.errors.motorDetails['vignette'] =
            vignetteAlreadyExistsErrorMessage;
        } else {
          newPageState.errors.motorDetails['vignette'] = '';
        }
      });
    }

    if (fieldName === 'nameOnLicenseEn') {
      autoSetNameOnLicenseAr(values.nameOnLicenseEn);
    }

    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IProposalMotorDetails,
    value: IProposalMotorDetails[keyof IProposalMotorDetails],
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);

    (newPageState.values.motorDetails as any)[fieldName] = value;

    if (fieldName === 'plateCode') {
      const filteredUsage = filteredUsageTypeList(
        value as string,
        lovs.usageTypes
      );
      setFilteredUsageTypes(filteredUsage);
    }

    if (fieldName === 'usageType') {
      const filteredBody = filteredBodyTypeList(
        value as string,
        lovs.bodyTypes
      );
      setFilteredBodyTypes(filteredBody);
    }

    if (fieldName === 'brand') {
      newPageState.values.motorDetails.model = '';
      newPageState.errors.motorDetails.model = '';
      newPageState.touched.motorDetails.model = false;
    }
    newPageState.errors.motorDetails[fieldName] = validateMotorDetailsField(
      fieldName,
      value,
      newPageState.values.motorDetails
    );

    if (touched) {
      newPageState.touched.costs[fieldName] = true;
    }

    onPageStateUpdate(newPageState);
  };

  const autoSetNameOnLicenseAr = (enTxt: string) => {
    setLoadingNameOnLicenseArState(true);
    DataService.get(`/api/prx/transliterate.qcri.org/en2ar/` + enTxt)
      .then(async (response) => {
        if (response.ok) {
          const result: { results: string } = await response.json();
          onPageStateUpdate((oldPageState) => ({
            ...oldPageState,
            values: {
              ...oldPageState.values,
              motorDetails: {
                ...oldPageState.values.motorDetails,
                nameOnLicenseAr: result.results,
              },
            },
          }));
        }
      })
      .catch(() => {
        onPageStateUpdate((oldPageState) => ({
          ...oldPageState,
          values: {
            ...oldPageState.values,
            motorDetails: {
              ...oldPageState.values.motorDetails,
              nameOnLicenseAr: enTxt,
            },
          },
        }));
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => {
        setLoadingNameOnLicenseArState(false);
      });
  };

  return (
    <WidgetPaper>
      <WidgetSection
        title="Motor Details"
        hasTitleSpecificDesign={false}
        classes={{
          container: classes.section,
        }}
        useSeparator={false}
      >
        <div className={classes.fieldRow}>
          <TextInputFormField
            key="nameOnLicenseEn"
            name="nameOnLicenseEn"
            title="Name on License (EN)*"
            placeholder="Name on License (EN)"
            value={values.nameOnLicenseEn}
            error={touched.nameOnLicenseEn ? errors.nameOnLicenseEn : ''}
            onBlur={() => onFieldBlur('nameOnLicenseEn')}
            onChange={(v) => onFieldUpdate('nameOnLicenseEn', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="nameOnLicenseAr"
            name="nameOnLicenseAr"
            title="Name on License (AR)*"
            placeholder="Name on License (AR)"
            value={values.nameOnLicenseAr}
            error={touched.nameOnLicenseAr ? errors.nameOnLicenseAr : ''}
            onBlur={() => onFieldBlur('nameOnLicenseAr')}
            onChange={(v) => onFieldUpdate('nameOnLicenseAr', v.target.value)}
            disabled={disabledForm}
            material={true}
            loader={loadingNameOnLicenseArState}
            rtlContent
          />

          {data.LineId.ExternalCode === '50' && (
            <>
              <TextInputFormField
                key="vignetteCode"
                name="vignetteCode"
                title="Vignette Code*"
                placeholder="Vignette Code"
                value={values.vignetteCode}
                error={touched.vignetteCode ? errors.vignetteCode : ''}
                onBlur={() => onFieldBlur('vignetteCode')}
                onChange={(v) => onFieldUpdate('vignetteCode', v.target.value)}
                disabled={disabledForm}
                material
              />

              <NumberInputFormField
                key="vignette"
                name="vignette"
                title="Vignette*"
                placeholder="Vignette"
                value={values.vignette}
                error={touched.vignette ? errors.vignette : ''}
                onBlur={() => onFieldBlur('vignette')}
                onChange={(v) => onFieldUpdate('vignette', v.target.value)}
                disabled={disabledForm}
                material
              />
            </>
          )}

          <SelectFormField
            key="plateCode"
            name="plateCode"
            title="Plate Code"
            placeholder="Plate Code"
            value={values.plateCode}
            error={touched.plateCode ? errors.plateCode : ''}
            onChange={(v) => onFieldUpdate('plateCode', v, true)}
            disabled={disabledForm}
            selectOptions={lovs.plateCodes}
            material
            // canClearSingleValueSelection={false}
            // customStyles={{
            //   labelStyles: classes.labelDropdown,
            // }}
          />

          <NumberInputFormField
            key="plateNumber"
            name="plateNumber"
            title="Plate Number"
            placeholder="Plate Number"
            value={values.plateNumber}
            error={touched.plateNumber ? errors.plateNumber : ''}
            onBlur={() => onFieldBlur('plateNumber')}
            onChange={(v) => onFieldUpdate('plateNumber', v.target.value)}
            disabled={disabledForm}
            material
          />

          <SelectFormField
            key="usageType"
            name="usageType"
            title="Usage*"
            placeholder="Usage"
            value={values.usageType}
            error={touched.usageType ? errors.usageType : ''}
            onChange={(v) => onFieldUpdate('usageType', v, true)}
            disabled={disabledForm}
            selectOptions={filteredUsageTypes}
            material
          />

          <SelectFormField
            key="bodyType"
            name="bodyType"
            title="Body*"
            placeholder="Body"
            value={values.bodyType}
            error={touched.bodyType ? errors.bodyType : ''}
            onChange={(v) => onFieldUpdate('bodyType', v, true)}
            disabled={disabledForm}
            selectOptions={filteredBodyTypes}
            material={true}
          />

          <SelectFormField
            key="engineType"
            name="engineType"
            title="Engine Type*"
            placeholder="Engine Type"
            value={values.engineType}
            error={touched.engineType ? errors.engineType : ''}
            onChange={(v) => onFieldUpdate('engineType', v, true)}
            disabled={disabledForm}
            selectOptions={lovs.engineTypes}
            material={true}
          />

          <SelectFormField
            key="brand"
            name="brand"
            title="Make*"
            placeholder="Make"
            value={values.brand}
            error={touched.brand ? errors.brand : ''}
            onChange={(v) => onFieldUpdate('brand', v, true)}
            onBlur={() => onFieldBlur('brand')}
            disabled={disabledForm}
            selectOptions={lovs.brands}
            material={true}
          />

          <SelectFormField
            key="model"
            name="model"
            title="Model*"
            placeholder="Model"
            value={values.model}
            error={touched.model ? errors.model : ''}
            onChange={(v) => onFieldUpdate('model', v, true)}
            disabled={disabledForm}
            selectOptions={modelLovs?.model || {}}
            material={true}
          />

          <SelectFormField
            key="color"
            name="color"
            title="Color"
            placeholder="Color"
            value={values.color}
            error={touched.color ? errors.color : ''}
            onChange={(v) => onFieldUpdate('color', v, true)}
            disabled={disabledForm}
            selectOptions={lovs?.colors || {}}
            material={true}
          />

          <NumberInputFormField
            key="yearOfMake"
            name="yearOfMake"
            title="Year of Make*"
            placeholder="Year of Make"
            value={values.yearOfMake}
            error={touched.yearOfMake ? errors.yearOfMake : ''}
            onBlur={() => onFieldBlur('yearOfMake')}
            onChange={(v) => onFieldUpdate('yearOfMake', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="motor"
            name="motor"
            title="Engine*"
            placeholder="Engine"
            value={values.motor}
            error={touched.motor ? errors.motor : ''}
            onBlur={() => onFieldBlur('motor')}
            onChange={(v) => onFieldUpdate('motor', v.target.value)}
            disabled={disabledForm}
            material
          />

          {['VAN', 'PICKUP', 'F_TANKER'].includes(values.bodyType) && (
            <NumberInputFormField
              key="weight"
              name="weight"
              title="Weight (in Tons)*"
              placeholder="Weight (in Tons)"
              value={values.weight}
              error={touched.weight ? errors.weight : ''}
              onBlur={() => onFieldBlur('weight')}
              onChange={(v) => onFieldUpdate('weight', v.target.value)}
              disabled={disabledForm}
              material
            />
          )}

          {['MOTORCYCLE'].includes(values.bodyType) && (
            <NumberInputFormField
              key="engineSize"
              name="engineSize"
              title="Engine Size (in cc)*"
              placeholder="Engine Size (in cc)"
              value={values.engineSize}
              error={touched.engineSize ? errors.engineSize : ''}
              onBlur={() => onFieldBlur('engineSize')}
              onChange={(v) => onFieldUpdate('engineSize', v.target.value)}
              disabled={disabledForm}
              material={true}
            />
          )}

          <NumberInputFormField
            key="seats"
            name="seats"
            title="Seating Capacity*"
            placeholder="Seating Capacity"
            value={values.seats}
            error={touched.seats ? errors.seats : ''}
            onBlur={() => onFieldBlur('seats')}
            onChange={(v) => onFieldUpdate('seats', v.target.value)}
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="chassis"
            name="chassis"
            title="Chassis*"
            placeholder="Chassis"
            value={values.chassis}
            error={touched.chassis ? errors.chassis : ''}
            onBlur={() => onFieldBlur('chassis')}
            onChange={(v) => onFieldUpdate('chassis', v.target.value)}
            disabled={disabledForm}
            material
          />

          <NumberInputFormField
            key="horsePower"
            name="horsePower"
            title="Horse Power*"
            placeholder="Horse Power"
            value={values.horsePower}
            error={touched.horsePower ? errors.horsePower : ''}
            onBlur={() => onFieldBlur('horsePower')}
            onChange={(v) => onFieldUpdate('horsePower', v.target.value)}
            disabled={disabledForm}
            material
          />

          {data.LineId.ExternalCode === '51' && (
            <>
              <CurrencyFormField
                key="carValue"
                name="carValue"
                title="Car Value*"
                placeholder="Car Value"
                value={values.carValue}
                error={touched.carValue ? errors.carValue : ''}
                onBlur={() => onFieldBlur('carValue')}
                onChange={(v) => onFieldUpdate('carValue', v.target.value)}
                disabled={disabledForm}
                material={true}
                currencySymbol={data?.PolicyCurrency?.Symbol}
              />

              <NumberInputFormField
                key="agencyRepairYear"
                name="agencyRepairYear"
                title="Agency Repair Years*"
                placeholder="Agency Repair Years"
                value={values.agencyRepairYear}
                error={touched.agencyRepairYear ? errors.agencyRepairYear : ''}
                onBlur={() => onFieldBlur('agencyRepairYear')}
                onChange={(v) =>
                  onFieldUpdate('agencyRepairYear', v.target.value)
                }
                disabled={disabledForm}
                material
              />

              <NumberInputFormField
                key="depreciationYears"
                name="depreciationYears"
                title="Depreciation Years*"
                placeholder="Depreciation Years"
                value={values.depreciationYears}
                error={
                  touched.depreciationYears ? errors.depreciationYears : ''
                }
                onBlur={() => onFieldBlur('depreciationYears')}
                onChange={(v) =>
                  onFieldUpdate('depreciationYears', v.target.value)
                }
                disabled={disabledForm}
                material
              />
            </>
          )}
        </div>
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ProposalMotorDetailsWidget;
